import { AxiosError, AxiosResponse } from 'axios';
import LayoutContext from 'components/Layout/LayoutContext';
import { EventTestAlert } from 'components/Modal/EventTestAlert/EventTestAlert';
import ReservationAlert from 'components/Modal/ReservationAlert';
import { AuthContext } from 'context/authContext/AuthContext';
import { RegisterContextActions } from 'context/registerContext/RegisterContextActions';
import {
  AssemblyModalities,
  AssemblyModalityText,
  AssemblyType,
  AssemblyTypeText,
  SituationValues,
  StepIds,
  StepStatus,
  Toggle,
} from 'helpers/enums/Assembleia';
import { getProfileByJWTRoles } from 'helpers/utils/AuthUtils';
import {
  isDateGreaterThanToday,
  isDateLessThanToday,
} from 'helpers/utils/DateUtils';

import {
  clsx,
  formatData,
  formatHora,
  isNullOrUndefined,
  returnDateAndHour,
} from 'helpers/utils/HTMLUtils';
import {
  getParsedJWTAssessorAccessToken,
  JWTAssessorJson,
} from 'helpers/utils/JWTUtils';
import { getUniqueKey } from 'helpers/utils/KeyUtils';
import { toRequest } from 'helpers/utils/RequestUtils';
import { onlyNumbers } from 'helpers/utils/StringUtils';
import { useRegisterContext } from 'hooks/useRegisterContext';
import { getAssembleiasAssessorBffEndpoint } from 'library/Apis';
import {
  AssessorCadastroEventoRoutePath,
  AssessorCadastroReuniaoRoutePath,
  AssessorRoutePath,
} from 'library/Routes';
import translation from 'locales/translation';
import moment from 'moment';
import {
  Button,
  ButtonThemes,
  Checkbox,
  CheckCircleIcon,
  ClockIcon,
  Col,
  Collapse,
  ConfirmModal,
  ControlLabel,
  DatePicker,
  EditIcon,
  IconButton,
  Input,
  ModalSizes,
  Row,
  ScreenSize,
  Select,
  Title,
  ToastTypes,
} from 'plataforma-fundacao-componentes';
import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';
import ApiService from 'services/ApiService';
import {
  getRetroactiveDay,
  postDataStep,
  postMakeEditable,
  putDataStep,
} from 'services/assessor';
import {
  AuthUserPerfilId,
  IAuthUserPerfil,
} from 'store/modules/auth/IAuthUserPerfil';
import { closeModal, openModal } from 'store/modules/ModalActions';
import { GetDataProps, makeEditable } from '../../Cadastro';
import { DataValidation, DataValidatorType } from './DataValidation';
import styles from './styles.module.scss';

type Changed = {
  startDateHybrid?: boolean;
  endDateHybrid?: boolean;
  startHourHybrid?: boolean;
  endHourHybrid?: boolean;
};

const dataPrefix = translation.Assessor.Evento.Cadastro.Etapas.Dados;

export default function Data(props: { reu?: boolean }) {
  const [sent, setSent] = useState<boolean>(false);
  const [day, setDay] = useState<number>();
  const [editButtonLoader, setEditButtonLoader] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [validator, setValidator] = useState<DataValidatorType | null>(null);
  const [changed, setChanged] = useState<Changed>({
    startDateHybrid: false,
    endDateHybrid: false,
    startHourHybrid: false,
    endHourHybrid: false,
  });
  const [calledOnce, hasCalledOnce] = useState<number>(0);
  const [data, setData] = useState<GetDataProps>();
  const history = useHistory();
  const dispatchAction = useDispatch();
  const { user } = useContext(AuthContext);
  const { showToast } = useContext(LayoutContext);
  const match = useRouteMatch<{ id?: string; etapa?: string }>();
  const {
    state,
    setStepId,
    setStepCompleted,
    dataStepSetFilter,
    dataStepSetFilterOld,
    surveyStepSetFilter,
    setStepStatus,
    changeState,
  } = useRegisterContext();

  const url = `${getAssembleiasAssessorBffEndpoint('1')}event/with-steps/${
    match.params.id
  }`;

  const finalDate = (date: string, hour: string) => {
    return `${date.split('/').reverse().join('-')}T${hour}:00`;
  };

  const completedStep =
    state.status === StepStatus.VISUALIZACAO &&
    state.eventStatus === SituationValues.RESERVABLE
      ? false
      : state.completed.find((item: { step: StepIds }) => item.step === 'dados')
          ?.completed && state.status !== StepStatus.EM_EDICAO;

  const validateModal = useCallback(
    (id?: string | null, err?: boolean) => {
      const mkey = getUniqueKey();
      if (!completedStep) {
        dispatchAction(
          openModal(ReservationAlert, {
            error: err,
            registeting: true,
            modalKey: mkey,
            preventEscExit: true,
            preventMaskExit: true,
            mobileOn: ScreenSize.xs,
            onCancel: () => {
              if (!err) {
                history.push(AssessorRoutePath);
              }
            },
            onConfirm: () => {
              return new Promise<void>(
                (resolve: () => void, reject: () => void) => {
                  postMakeEditable(Number(id))
                    .then(() => {
                      setStepStatus(StepStatus.EM_EDICAO);
                      changeState(
                        RegisterContextActions.SET_EVENT_STATUS,
                        SituationValues.EDITABLE
                      );
                      if (props.reu) {
                        history.push(
                          `${AssessorCadastroReuniaoRoutePath}${id}/etapas/entidade`
                        );
                      } else {
                        history.push(
                          `${AssessorCadastroEventoRoutePath}${id}/etapas/entidade`
                        );
                      }
                      setStepCompleted(
                        state.completed.map(
                          (item: { step: StepIds; completed: boolean }) =>
                            item.step === StepIds.data
                              ? { ...item, completed: true }
                              : item
                        )
                      );
                      dataStepSetFilterOld(state.data.campos);

                      resolve();
                    })
                    .catch((error: AxiosError) => {
                      if (error) {
                        reject();
                        showToast({
                          pauseOnFocusLoss: true,
                          theme: ToastTypes.Error,
                          showStatusBar: true,
                          timeout: 4000,
                          label: translation.Toasts.Errors.Keys.errMakeEditable,
                        });
                      }
                    });
                }
              );
            },
          })
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      completedStep,
      dispatchAction,
      sent,
      validator,
      state.status,
      state.data.campos,
    ]
  );

  const inputDate = useMemo(() => {
    return (state.data.campos.startDate && state.data.campos.startHour) ||
      (state.data.campos.hybridStartDate && state.data.campos.hybridStartHour)
      ? new Date(
          [AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL].includes(
            state.data.campos.modality!
          )
            ? finalDate(
                state.data.campos.hybridStartDate,
                state.data.campos.hybridStartHour
              )
            : finalDate(
                state.data.campos.startDate,
                state.data.campos.startHour
              )
        )
      : 'Invalid Date';
  }, [
    state.data.campos.hybridStartDate,
    state.data.campos.hybridStartHour,
    state.data.campos.modality,
    state.data.campos.startDate,
    state.data.campos.startHour,
  ]);

  const inputsChanged = useMemo(() => {
    if (
      [
        AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
        AssemblyModalities.HIBRIDO_SIMULTANEO,
        AssemblyModalities.ONLINE,
      ].includes(state.data.campos.modality!)
    ) {
      if (
        Number(state.data.campos.tmpQuantityAssociates) !==
          Number(state.data.campos.quantityAssociates) ||
        state.data.campos.tmpStartDate !== state.data.campos.startDate ||
        state.data.campos.tmpStartHour !== state.data.campos.startHour
      ) {
        return isDateGreaterThanToday(inputDate);
      }
    } else if (
      [AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL].includes(
        state.data.campos.modality!
      )
    ) {
      if (
        Number(state.data.campos.tmpQuantityAssociates) !==
          Number(state.data.campos.quantityAssociates) ||
        state.data.campos.tmpStartDate !== state.data.campos.hybridStartDate ||
        state.data.campos.tmpStartHour !== state.data.campos.hybridStartHour
      ) {
        return isDateGreaterThanToday(inputDate);
      }
    }

    return false;
  }, [
    inputDate,
    state.data.campos.hybridStartDate,
    state.data.campos.hybridStartHour,
    state.data.campos.modality,
    state.data.campos.quantityAssociates,
    state.data.campos.startDate,
    state.data.campos.startHour,
    state.data.campos.tmpQuantityAssociates,
    state.data.campos.tmpStartDate,
    state.data.campos.tmpStartHour,
  ]);

  const sendData = useMemo(() => {
    const data = {
      ...(match.params.id! &&
        match.params.id.length === 5 && {
          eventId: Number(match.params.id!),
        }),
      title: state.data.campos.title,
      cooperative: state.data.campos.cooperative!,
      type: state.data.campos.type!,
      modality: state.data.campos.modality!,
      description: state.data.campos.description,
      link: state.data.campos.link,
      startDate: finalDate(
        state.data.campos.startDate,
        state.data.campos.startHour
      ),
      endDate: finalDate(state.data.campos.endDate, state.data.campos.endHour),
      microsoftTeams:
        isNullOrUndefined(state.data.campos.microsoftTeams!) &&
        state.data.campos.modality === AssemblyModalities.OFFLINE
          ? Toggle.DISABLED
          : state.data.campos.microsoftTeams,
      flgEventTest: state.data.campos.flgEventTest,
      hybridStartDate:
        (state.data.campos.hybridStartDate &&
          finalDate(
            state.data.campos.hybridStartDate,
            state.data.campos.hybridStartHour
          )) ??
        '',
      hybridEndDate:
        (state.data.campos.hybridEndDate &&
          finalDate(
            state.data.campos.hybridEndDate,
            state.data.campos.hybridEndHour
          )) ??
        '',
      ...(props.reu && {
        inviteCoordinator: true,
        inviteAssociate: state.data.campos.flagAssociate,
      }),
      ...(isDateGreaterThanToday(inputDate) && {
        quantityAssociates:
          state.data.campos.modality === AssemblyModalities.OFFLINE
            ? null
            : state.data.campos.quantityAssociates
            ? Number(parseInt(state.data.campos.quantityAssociates))
            : Number(parseInt(state.data.campos.tmpQuantityAssociates!)),
      }),
    };

    return data;
  }, [
    match.params.id,
    state.data.campos.title,
    state.data.campos.cooperative,
    state.data.campos.type,
    state.data.campos.modality,
    state.data.campos.description,
    state.data.campos.link,
    state.data.campos.startDate,
    state.data.campos.startHour,
    state.data.campos.endDate,
    state.data.campos.endHour,
    state.data.campos.microsoftTeams,
    state.data.campos.flgEventTest,
    state.data.campos.hybridStartDate,
    state.data.campos.hybridStartHour,
    state.data.campos.hybridEndDate,
    state.data.campos.hybridEndHour,
    state.data.campos.flagAssociate,
    state.data.campos.quantityAssociates,
    state.data.campos.tmpQuantityAssociates,
    props.reu,
    inputDate,
  ]);

  const handleSubmit = useCallback(async () => {
    if (!completedStep) {
      if (loading) return;
      if (!sent) setSent(true);
      if (!validator?.allIsValid()) return;
      setLoading(true);

      if (
        [SituationValues.RESERVABLE, SituationValues.EDITABLE].includes(
          state.eventStatus!
        ) ||
        state.status === StepStatus.EM_EDICAO
      ) {
        putDataStep(sendData)
          .then((resp: AxiosResponse) => {
            setSent(false);
            setLoading(false);
            setStepCompleted(
              state.completed.map(
                (item: { step: StepIds; completed: boolean }) =>
                  item.step === StepIds.data
                    ? { ...item, completed: true }
                    : item
              )
            );

            dataStepSetFilterOld(state.data.campos);
            surveyStepSetFilter({ templatesOld: state.survey.templates });

            if (
              ![AssemblyModalities.OFFLINE].includes(
                state.data.campos.modality!
              ) &&
              isDateGreaterThanToday(inputDate)
            ) {
              if (
                Number(state.data.campos.quantityAssociates) !==
                Number(state.data.campos.tmpQuantityAssociates)
              ) {
                dataStepSetFilter({
                  quantityAssociates: Number(
                    state.data.campos.quantityAssociates
                  ),
                  tmpQuantityAssociates: Number(
                    state.data.campos.quantityAssociates
                  ),
                });
                dataStepSetFilterOld({
                  quantityAssociates: Number(
                    state.data.campos.quantityAssociates
                  ),
                  tmpQuantityAssociates: Number(
                    state.data.campos.quantityAssociates
                  ),
                });
              }
            }

            if (
              state.eventStatus === SituationValues.RESERVABLE &&
              inputsChanged
            ) {
              validateModal(resp.data.id, false);
            } else if (
              state.eventStatus === SituationValues.RESERVABLE &&
              (state.status === StepStatus.EM_EDICAO ||
                [AssemblyModalities.OFFLINE].includes(
                  state.data.campos.modality!
                ) ||
                isDateLessThanToday(inputDate))
            ) {
              postMakeEditable(Number(resp.data.id))
                .then(() => {
                  setStepStatus(StepStatus.EM_EDICAO);
                  changeState(
                    RegisterContextActions.SET_EVENT_STATUS,
                    SituationValues.EDITABLE
                  );
                })
                .catch((error: any) => {
                  if (
                    error!?.response!?.data.message === 'event.actionDenied'
                  ) {
                    setStepStatus(StepStatus.EM_EDICAO);
                    changeState(
                      RegisterContextActions.SET_EVENT_STATUS,
                      SituationValues.EDITABLE
                    );
                    if (props.reu) {
                      history.push(
                        `${AssessorCadastroReuniaoRoutePath}${resp.data.id}/etapas/entidade`
                      );
                    } else {
                      history.push(
                        `${AssessorCadastroEventoRoutePath}${resp.data.id}/etapas/entidade`
                      );
                    }
                  } else {
                    if (error) {
                      showToast({
                        pauseOnFocusLoss: true,
                        theme: ToastTypes.Error,
                        showStatusBar: true,
                        timeout: 4000,
                        label: translation.Toasts.Errors.Keys.errMakeEditable,
                      });
                    }
                  }
                });
            } else {
              setStepStatus(StepStatus.EM_EDICAO);
              setStepCompleted(
                state.completed.map(
                  (item: { step: StepIds; completed: boolean }) =>
                    item.step === StepIds.data
                      ? { ...item, completed: true }
                      : item
                )
              );
              changeState(
                RegisterContextActions.SET_EVENT_STATUS,
                SituationValues.EDITABLE
              );

              dataStepSetFilterOld(state.data.campos);

              if (state.showAlert) {
                history.push(state.toPage);
                if (
                  ![AssemblyModalities.OFFLINE].includes(
                    state.data.campos.modality!
                  ) &&
                  isDateGreaterThanToday(inputDate)
                ) {
                  if (
                    Number(state.data.campos.quantityAssociates) !==
                    Number(state.data.campos.tmpQuantityAssociates)
                  ) {
                    dataStepSetFilter({
                      quantityAssociates: Number(
                        state.data.campos.quantityAssociates
                      ),
                      tmpQuantityAssociates: Number(
                        state.data.campos.quantityAssociates
                      ),
                    });
                    dataStepSetFilterOld({
                      quantityAssociates: Number(
                        state.data.campos.quantityAssociates
                      ),
                      tmpQuantityAssociates: Number(
                        state.data.campos.quantityAssociates
                      ),
                    });
                  }
                }
              } else {
                dataStepSetFilter({
                  quantityAssociates: Number(
                    state.data.campos.quantityAssociates
                  ),
                  tmpQuantityAssociates: Number(
                    state.data.campos.quantityAssociates
                  ),
                });
                dataStepSetFilterOld({
                  quantityAssociates: Number(
                    state.data.campos.quantityAssociates
                  ),
                  tmpQuantityAssociates: Number(
                    state.data.campos.quantityAssociates
                  ),
                });

                if (
                  state.data.campos.modality &&
                  [AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL].includes(
                    state.data.campos.modality
                  ) &&
                  state.data.campos.startDate &&
                  state.data.campos.startHour &&
                  state.data.campos.hybridStartDate &&
                  state.data.campos.hybridStartHour
                ) {
                  dataStepSetFilterOld({
                    tmpStartDate: state.data.campos.hybridStartDate,
                    tmpStartHour: state.data.campos.hybridStartHour,
                  });
                }
                if (props.reu) {
                  history.push(
                    `${AssessorCadastroReuniaoRoutePath}${resp.data.id}/etapas/entidade`
                  );
                } else {
                  history.push(
                    `${AssessorCadastroEventoRoutePath}${resp.data.id}/etapas/entidade`
                  );
                }
              }
            }
          })
          .catch((error) => {
            if (
              [AssemblyModalities.OFFLINE].includes(
                state.data.campos.modality!
              ) ||
              isDateLessThanToday(inputDate)
            ) {
              showToast({
                pauseOnFocusLoss: true,
                theme: ToastTypes.Error,
                showStatusBar: true,
                timeout: 4000,
                label:
                  translation.Toasts.Errors.Keys[
                    `${
                      (error as AxiosError<Error>).response?.data.message ||
                      (error as AxiosError<Error>).response?.data ||
                      (error as AxiosError<Error>).message
                    }` as keyof typeof translation.Toasts.Errors.Keys
                  ] ?? translation.Toasts.Errors.defaultAlternative,
              });
            } else {
              validateModal(null, true);
            }

            setSent(false);
            setLoading(false);
          })
          .finally(() => {
            if (
              JSON.stringify(state.survey.templates) !==
              JSON.stringify(state.survey.templatesOld)
            ) {
              surveyStepSetFilter({
                enabled: false,
                enabledOld: false,
                template: null,
                templateOld: null,
              });

              setStepCompleted(
                state.completed.map(
                  (item: { step: StepIds; completed: boolean }) =>
                    item.step === StepIds.survey
                      ? { ...item, completed: false }
                      : item
                )
              );
            }
          });
      } else {
        postDataStep(sendData)
          .then((resp: AxiosResponse) => {
            setSent(false);
            setLoading(false);
            setStepStatus(StepStatus.EM_EDICAO);

            changeState(
              RegisterContextActions.DATA_FILTER_SET_OLD,
              state.data.campos
            );

            if (
              [AssemblyModalities.OFFLINE].includes(state.data.campos.modality!)
            ) {
              changeState(
                RegisterContextActions.SET_EVENT_STATUS,
                SituationValues.EDITABLE
              );
            } else {
              if (isDateLessThanToday(inputDate)) {
                changeState(
                  RegisterContextActions.SET_EVENT_STATUS,
                  SituationValues.EDITABLE
                );
              } else {
                changeState(
                  RegisterContextActions.SET_EVENT_STATUS,
                  SituationValues.RESERVABLE
                );
              }
            }

            setStepCompleted(
              state.completed.map(
                (item: { step: StepIds; completed: boolean }) =>
                  item.step === StepIds.data
                    ? { ...item, completed: true }
                    : item
              )
            );

            if (
              [AssemblyModalities.OFFLINE].includes(
                state.data.campos.modality!
              ) ||
              isDateLessThanToday(inputDate)
            ) {
              if (state.showAlert) {
                history.push(state.toPage);
              } else {
                if (props.reu) {
                  history.push(
                    `${AssessorCadastroReuniaoRoutePath}${resp.data.id}/etapas/entidade`
                  );
                } else {
                  history.push(
                    `${AssessorCadastroEventoRoutePath}${resp.data.id}/etapas/entidade`
                  );
                }
              }
            } else {
              validateModal(resp.data.id, false);
            }
          })
          .catch((error) => {
            if (
              [AssemblyModalities.OFFLINE].includes(
                state.data.campos.modality!
              ) ||
              isDateLessThanToday(inputDate)
            ) {
              showToast({
                pauseOnFocusLoss: true,
                theme: ToastTypes.Error,
                showStatusBar: true,
                timeout: 4000,
                label:
                  translation.Toasts.Errors.Keys[
                    `${
                      (error as AxiosError<Error>).response?.data.message ||
                      (error as AxiosError<Error>).response?.data ||
                      (error as AxiosError<Error>).message
                    }` as keyof typeof translation.Toasts.Errors.Keys
                  ] ?? translation.Toasts.Errors.defaultAlternative,
              });
            } else {
              validateModal(null, true);
            }

            setSent(false);
            setLoading(false);
          });
      }
    } else {
      if (props.reu) {
        history.push(
          `${AssessorCadastroReuniaoRoutePath}${match.params.id}/etapas/entidade`
        );
      } else {
        history.push(
          `${AssessorCadastroEventoRoutePath}${match.params.id}/etapas/entidade`
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    completedStep,
    loading,
    sent,
    validator,
    state.eventStatus,
    state.status,
    state.data.campos,
    state.completed,
    state.showAlert,
    state.toPage,
    props.reu,
    history,
    match.params.id,
    sendData,
    setStepCompleted,
    dataStepSetFilterOld,
    inputDate,
    inputsChanged,
    dataStepSetFilter,
    validateModal,
    setStepStatus,
    changeState,
    showToast,
  ]);

  const parsedToken: JWTAssessorJson | null = getParsedJWTAssessorAccessToken(
    user!.access_token
  );
  const perfil: IAuthUserPerfil | null = parsedToken
    ? getProfileByJWTRoles(parsedToken.roles)
    : null;

  const currentDate = new Date();

  const shouldShowQuantityInput = useMemo(() => {
    if (![AssemblyModalities.OFFLINE].includes(state.data.campos.modality!)) {
      return true;
    }

    return false;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.data.campos.modality]);

  const lessThanCurrentDate = useMemo(() => {
    if (state.data.campos.modality) {
      if (
        (state.data.campos.startDate.length === 10 &&
          state.data.campos.startHour.length === 5) ||
        (state.data.campos.hybridStartDate.length === 10 &&
          state.data.campos.hybridStartHour.length === 5)
      ) {
        if (isDateLessThanToday(inputDate)) {
          return true;
        }
      }
    } else {
      return false;
    }

    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputDate, currentDate]);

  const loadingData = useMemo(() => {
    return (
      state.data.cooperativeLoading ||
      state.data.modalitiesLoading ||
      state.data.tiposLoading
    );
  }, [
    state.data.cooperativeLoading,
    state.data.modalitiesLoading,
    state.data.tiposLoading,
  ]);

  useEffect(() => {
    setStepId(StepIds.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      state.data.campos.modality &&
      [AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL].includes(
        state.data.campos.modality
      ) &&
      state.data.campos.startDate &&
      state.data.campos.startHour &&
      state.data.campos.hybridStartDate &&
      state.data.campos.hybridStartHour
    ) {
      dataStepSetFilter({
        tmpStartDate: state.data.campos.hybridStartDate,
        tmpStartHour: state.data.campos.hybridStartHour,
      });
      dataStepSetFilterOld({
        tmpStartDate: state.data.campos.hybridStartDate,
        tmpStartHour: state.data.campos.hybridStartHour,
      });
    }

    if (
      state.data.campos.modality &&
      [AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL].includes(
        state.data.campos.modality
      ) &&
      state.data.campos.startDate &&
      state.data.campos.startHour &&
      !state.data.campos.hybridStartDate &&
      !state.data.campos.hybridStartHour
    ) {
      dataStepSetFilter({
        tmpStartDate: state.data.campos.startDate,
        tmpStartHour: state.data.campos.startHour,
      });
      dataStepSetFilterOld({
        tmpStartDate: state.data.campos.startDate,
        tmpStartHour: state.data.campos.startHour,
      });
    }

    if (
      state.data.campos.modality &&
      [
        AssemblyModalities.ONLINE,
        AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
        AssemblyModalities.HIBRIDO_SIMULTANEO,
      ].includes(state.data.campos.modality) &&
      state.data.campos.startDate &&
      state.data.campos.startHour &&
      state.data.campos.hybridStartDate &&
      state.data.campos.hybridStartHour
    ) {
      dataStepSetFilter({
        tmpStartDate: state.data.campos.startDate,
        tmpStartHour: state.data.campos.startHour,
      });
      dataStepSetFilterOld({
        tmpStartDate: state.data.campos.startDate,
        tmpStartHour: state.data.campos.startHour,
      });
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.data.campos.modality]);

  useEffect(() => {
    setValidator(DataValidation(state, props.reu, day));
  }, [props.reu, state, day]);

  useEffect(() => {
    if (!completedStep) {
      if (validator && validator.allIsValid()) {
        setSent(false);
      }
    }
  }, [completedStep, validator, validator?.allIsValid]);

  useEffect(() => {
    if (state.data.campos.modality) {
      if (
        [AssemblyModalities.OFFLINE, AssemblyModalities.ONLINE].includes(
          state.data.campos.modality
        )
      ) {
        dataStepSetFilter({
          hybridStartDate: '',
          hybridEndDate: '',
          hybridStartHour: '',
          hybridEndHour: '',
        });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.data.campos.modality]);

  useEffect(() => {
    if (
      calledOnce <= 0 &&
      [StepStatus.VISUALIZACAO, StepStatus.EM_EDICAO].includes(state.status) &&
      [
        AssemblyModalities.HIBRIDO_SIMULTANEO,
        AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
      ].includes(state.data.campos.modality!)
    ) {
      toRequest(ApiService.get, [url])
        .then((infos) => {
          hasCalledOnce((e) => e + 1);
          setData(infos.data);
        })
        .catch((error) => {
          if (
            error.response.status === 417 &&
            error.response.data.message === 'event.isDeleted'
          ) {
            showToast({
              pauseOnFocusLoss: true,
              theme: ToastTypes.Error,
              showStatusBar: true,
              timeout: 4000,
              label:
                translation.Toasts.Errors.Keys[
                  `${
                    (error as AxiosError<Error>).response?.data.message ||
                    (error as AxiosError<Error>).response?.data ||
                    (error as AxiosError<Error>).message
                  }` as keyof typeof translation.Toasts.Errors.Keys
                ] ?? translation.Toasts.Errors.defaultAlternative,
            });
          } else {
            showToast({
              pauseOnFocusLoss: true,
              theme: ToastTypes.Error,
              showStatusBar: true,
              timeout: 4000,
              label:
                translation.Toasts.Errors.Keys[
                  `${
                    (error as AxiosError<Error>).response?.data.message ||
                    (error as AxiosError<Error>).response?.data ||
                    (error as AxiosError<Error>).message
                  }` as keyof typeof translation.Toasts.Errors.Keys
                ] ?? translation.Toasts.Errors.defaultAlternative,
            });
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.data.campos.modality]);

  useEffect(() => {
    if (data) {
      if (
        [
          AssemblyModalities.HIBRIDO_SIMULTANEO,
          AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
        ].includes(state.data.campos.modality!)
      ) {
        const formatedHybridStartDate = returnDateAndHour(
          data.data.hybridStartDate
        );
        const formatedHybridEndDate = returnDateAndHour(
          data.data.hybridEndDate
        );

        dataStepSetFilter({
          hybridStartDate: formatedHybridStartDate.formatedDate,
          hybridEndDate: formatedHybridEndDate.formatedDate,
          hybridStartHour: formatedHybridStartDate.formatedHour,
          hybridEndHour: formatedHybridEndDate.formatedHour,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.data.campos.modality]);

  const eventTestModal = useCallback(() => {
    if (!sent) setSent(true);
    if (!validator?.allIsValid()) return;

    const mk = getUniqueKey();

    dispatchAction(
      openModal(EventTestAlert, {
        modalKey: mk,
        size: ModalSizes.Large,
        mobileOn: ScreenSize.xs,
        onlyConfirm: false,
        preventEscExit: false,
        preventMaskExit: true,
        onClose: () => {
          dispatchAction(closeModal(mk));
        },
        onCancel: () => {
          dispatchAction(closeModal(mk));
        },
        onConfirm: () => {
          handleSubmit();
        },
      })
    );
  }, [dispatchAction, handleSubmit, sent, validator]);

  useEffect(() => {
    if (
      state.showAlert &&
      (state.status === StepStatus.EM_EDICAO ||
        state.eventStatus === SituationValues.EDITABLE)
    ) {
      const mk = getUniqueKey();

      dispatchAction(
        openModal(ConfirmModal, {
          modalKey: mk,
          size: ModalSizes.Large,
          title: 'Confirmação',
          mobileOn: ScreenSize.xs,
          children:
            'Há modificações que ainda não foram salvas. Você deseja salvar essas alterações antes de alterar a etapa atual?',
          onClose: () => {
            changeState(RegisterContextActions.SET_SHOW_ALERT, false);
            dispatchAction(closeModal(mk));
          },
          onCancel: () => {
            changeState(RegisterContextActions.SET_SHOW_ALERT, false);
            history.push(state.toPage);
            dataStepSetFilter(state.dataOld.campos!);
            dispatchAction(closeModal(mk));
          },
          onConfirm: () => {
            changeState(RegisterContextActions.SET_SHOW_ALERT, false);
            dispatchAction(closeModal(mk));

            if (
              Number(state.data.campos.quantityAssociates) !==
              Number(state.data.campos.tmpQuantityAssociates)
            ) {
              dataStepSetFilter({
                quantityAssociates: Number(
                  state.data.campos.quantityAssociates
                ),
                tmpQuantityAssociates: Number(
                  state.data.campos.quantityAssociates
                ),
              });
              dataStepSetFilterOld({
                quantityAssociates: Number(
                  state.data.campos.quantityAssociates
                ),
                tmpQuantityAssociates: Number(
                  state.data.campos.quantityAssociates
                ),
              });
            }

            handleSubmit();
          },
          language: {
            confirm: 'Salvar',
            cancel: 'Não salvar',
          },
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.showAlert]);

  useEffect(() => {
    if (
      state.data.campos.title.includes('[CÓPIA]') ||
      [StepStatus.EM_EDICAO, StepStatus.VISUALIZACAO].includes(state.status) ||
      state.eventStatus === SituationValues.RESERVABLE
    ) {
      setChanged({
        startDateHybrid: true,
        endDateHybrid: true,
        startHourHybrid: true,
        endHourHybrid: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.data.campos.title, state.status]);

  useEffect(() => {
    if (match.params.id && match.params.id.length > 5) {
      const formatted = moment(match.params.id).format('DD/MM/yyyy');

      dataStepSetFilter({
        startDate: formatted,
      });

      if (state.data.campos.startDate.length) {
        const url = window.location.href;
        const regex = /\/cadastro\/([^/]+)/;
        const match = url.match(regex);

        if (match) {
          const urlDate = match[1];
          const newUrl = url.replace(`/${urlDate}`, '');
          window.history.replaceState({}, document.title, newUrl);
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.data.campos.startDate]);

  useEffect(() => {
    if ([AssemblyModalities.OFFLINE].includes(state.data.campos.modality!)) {
      getRetroactiveDay().then((resp) => setDay(resp.data));
    }
  }, [state.data.campos.modality]);

  return (
    <Row className={styles.root}>
      <Row className={styles.title_content}>
        <Row></Row>
        <Col cols={[12, 12, 6, 6, 6]}>
          <Row>
            <Title>{dataPrefix.DadosEvento(props.reu)}</Title>
            <Col noPadding style={{ paddingTop: '8px' }}>
              <ControlLabel
                control={
                  <Checkbox
                    disabled={
                      state.eventStatus !== null ||
                      state.status !== StepStatus.CRIACAO
                    }
                    onChange={(value: boolean) => {
                      dataStepSetFilter({
                        flgEventTest: value,
                      });
                    }}
                    value={state.data.campos.flgEventTest}
                  />
                }
                label={
                  props.reu
                    ? dataPrefix.Campos.EventoTeste.LabelREU
                    : dataPrefix.Campos.EventoTeste.Label
                }
              />
            </Col>
            <Col noPadding style={{ paddingTop: '16px' }}>
              <span
                className={clsx(styles.test, {
                  [styles.disabled]:
                    state.eventStatus !== null ||
                    state.status !== StepStatus.CRIACAO,
                })}
              >
                {dataPrefix.EventoTeste}
              </span>
            </Col>
          </Row>
        </Col>

        {[SituationValues.AVAILABLE, SituationValues.IN_PREPARATION].includes(
          state.eventStatus!
        ) &&
        ((state.status !== StepStatus.EM_EDICAO &&
          [AuthUserPerfilId.PertencerConsulta].includes(perfil!?.id!) &&
          props.reu) ||
          ![AuthUserPerfilId.PertencerConsulta].includes(perfil!?.id!)) ? (
          <Col cols={[12, 12, 6, 6, 6]} end buttonActionsCol>
            <Button
              theme={ButtonThemes.Primary}
              leftIcon={<EditIcon />}
              onClick={() => {
                setEditButtonLoader(true);
                makeEditable(
                  String(match.params.id),
                  () => {
                    setEditButtonLoader(false);
                    setStepStatus(StepStatus.EM_EDICAO);
                    changeState(
                      RegisterContextActions.SET_EVENT_STATUS,
                      SituationValues.EDITABLE
                    );
                  },
                  (error) => {
                    setEditButtonLoader(false);
                    showToast({
                      pauseOnFocusLoss: true,
                      theme: ToastTypes.Error,
                      showStatusBar: true,
                      timeout: 4000,
                      label:
                        translation.Toasts.Errors.Keys[
                          `${
                            (error as AxiosError<Error>).response?.data
                              .message ||
                            (error as AxiosError<Error>).response?.data ||
                            (error as AxiosError<Error>).message
                          }` as keyof typeof translation.Toasts.Errors.Keys
                        ] ?? translation.Toasts.Errors.defaultAlternative,
                    });
                  }
                );
              }}
              disabled={loadingData || editButtonLoader}
              loading={editButtonLoader}
            >
              {dataPrefix.Buttons.Editar}
            </Button>
          </Col>
        ) : undefined}
      </Row>
      <Row>
        <Col cols={[12, 12, 6, 6, 6]}>
          <Input
            data-testid={'input-title'}
            value={state.data.campos.title ?? ''}
            label={dataPrefix.Campos.Titulo.Label(props.reu)}
            placeholder={dataPrefix.Campos.Titulo.Placeholder(props.reu)}
            maxLength={100}
            onChange={(e) => {
              dataStepSetFilter({
                title: e.target.value,
              });
            }}
            counter
            autoFocus={!completedStep}
            error={sent && !!validator && validator.titleIsValid().error}
            helperText={
              sent && !!validator ? validator.titleIsValid().helperText : ''
            }
            disabled={completedStep}
          />
        </Col>

        <Col cols={[12, 12, 6, 6, 6]}>
          <Select
            data-testid={'select-cooperativa'}
            label={dataPrefix.Campos.Cooperativa.Label}
            placeholder={dataPrefix.Campos.Cooperativa.Placeholder}
            options={state.data.cooperativeOptions}
            value={state.data.campos.cooperative!}
            onChange={(coop) => {
              dataStepSetFilter({
                cooperative: coop,
              });
            }}
            panelFilter={state.data.cooperativeOptions.length > 5}
            preSelectUniqueOption
            loading={
              state.status !== StepStatus.VISUALIZACAO &&
              state.data.cooperativeLoading
            }
            disabled={
              state.status === StepStatus.VISUALIZACAO ||
              state.status === StepStatus.EM_EDICAO ||
              completedStep ||
              state.data.cooperativeLoading ||
              state.data.cooperativeOptions.length === 1
            }
            clearButton={
              !completedStep &&
              state.data.cooperativeOptions.length > 1 &&
              state.status !== StepStatus.EM_EDICAO &&
              state.status !== StepStatus.VISUALIZACAO
            }
            error={sent && !!validator && validator.coopIsValid().error}
            helperText={
              sent && !!validator ? validator.coopIsValid().helperText : ''
            }
          />
        </Col>
        <Col
          cols={
            [AssemblyModalities.OFFLINE].includes(state.data.campos.modality!)
              ? [12, 12, 12, 3, 3]
              : [12, 12, 12, 4, 4]
          }
        >
          <Select
            data-testid={'select-type'}
            value={state.data.campos.type}
            options={state.data.type
              .filter((item: AssemblyType) =>
                props.reu ? item === 'REU' : item !== 'REU'
              )
              .map((item: AssemblyType) => ({
                label: AssemblyTypeText[item as keyof typeof AssemblyTypeText],
                value: item,
              }))}
            onChange={(e) => {
              dataStepSetFilter({
                type: e,
                modality: '',
                link: '',
              });
              setStepCompleted(
                state.completed.map(
                  (item: { step: StepIds; completed: boolean }) =>
                    item.step === StepIds.survey
                      ? { ...item, completed: false }
                      : item
                )
              );
            }}
            label={dataPrefix.Campos.Tipo.Label(props.reu)}
            placeholder={dataPrefix.Campos.Tipo.Placeholder(props.reu)}
            error={sent && !!validator && validator.assemblyTypeIsValid().error}
            loading={state.data.tiposLoading}
            helperText={
              sent && !!validator
                ? validator.assemblyTypeIsValid().helperText
                : ''
            }
            disabled={props.reu || completedStep || state.data.tiposLoading}
            clearButton={props.reu ? false : !completedStep}
            preSelectUniqueOption={props.reu}
          />
        </Col>
        <Col
          cols={
            [AssemblyModalities.OFFLINE].includes(state.data.campos.modality!)
              ? [12, 12, 12, 3, 3]
              : [12, 12, 12, 4, 4]
          }
        >
          <Select
            data-testid={'input-modalities'}
            options={
              state.data.campos.type
                ? (
                    state.data.link[
                      state.data.campos.type
                    ] as AssemblyModalities[]
                  )
                    .map((item: AssemblyModalities) => {
                      return {
                        label:
                          AssemblyModalityText[
                            item as keyof typeof AssemblyModalityText
                          ],
                        value: item,
                      };
                    })
                    .sort(
                      (
                        a: { label: string; value: string },
                        b: { label: string; value: string }
                      ) => (a.label < b.label ? -1 : 1)
                    )
                : []
            }
            label={dataPrefix.Campos.Modalidade.Label(props.reu)}
            placeholder={dataPrefix.Campos.Modalidade.Placeholder(props.reu)}
            disabled={completedStep || !state.data.campos.type}
            value={state.data.campos.modality}
            loading={state.data.modalitiesLoading}
            onChange={(e) => {
              dataStepSetFilter({
                modality: e,
                link:
                  e !== AssemblyModalities.OFFLINE
                    ? state.data.campos.link
                    : '',
                quantityAssociates:
                  e !== AssemblyModalities.OFFLINE
                    ? state.data.campos.quantityAssociates
                    : null,
                microsoftTeams:
                  e === AssemblyModalities.OFFLINE
                    ? Toggle.DISABLED
                    : undefined,
                hybridStartDate: state.data.campos.startDate,
                hybridStartHour: state.data.campos.startHour,
                hybridEndDate: state.data.campos.endDate,
                hybridEndHour: state.data.campos.endHour,
              });
            }}
            error={
              sent && !!validator && validator.assemblyModalityIsValid().error
            }
            helperText={
              sent && !!validator
                ? validator.assemblyModalityIsValid().helperText
                : ''
            }
            clearButton={!completedStep}
          />
        </Col>
        <Col
          cols={
            [AssemblyModalities.OFFLINE].includes(state.data.campos.modality!)
              ? [12, 12, 12, 6, 6]
              : [12, 12, 12, 4, 4]
          }
          noPaddingTop
          className={styles.summoned_container}
        >
          <Row>
            <Col>
              <p>{dataPrefix.Campos.Convocados.Titulo}</p>
            </Col>
            <Col gap={16}>
              <ControlLabel
                control={
                  <Checkbox
                    disabled
                    value={
                      props.reu ||
                      [
                        AssemblyType.AGN,
                        AssemblyType.AGO,
                        AssemblyType.AGE,
                        AssemblyType.AOE,
                      ].includes(state.data.campos.type!)
                    }
                  />
                }
                label={dataPrefix.Campos.Convocados.Coordenadores}
              />
              <ControlLabel
                control={
                  <Checkbox
                    disabled={
                      !completedStep
                        ? props.reu &&
                          [
                            AuthUserPerfilId.PertencerAdmin,
                            AuthUserPerfilId.PertencerGestao,
                            AuthUserPerfilId.PertencerConsulta,
                          ].includes(perfil!?.id!)
                          ? !state.data.campos.type
                          : true
                        : true
                    }
                    value={
                      props.reu
                        ? state.data.campos.flagAssociate
                        : [AssemblyType.AGN].includes(state.data.campos.type!)
                    }
                    onChange={(e) => dataStepSetFilter({ flagAssociate: e })}
                  />
                }
                label={dataPrefix.Campos.Convocados.Associados}
              />
              {[AssemblyModalities.OFFLINE].includes(
                state.data.campos.modality!
              ) ? (
                <Fragment>
                  <div
                    style={{
                      width: '2px',
                      height: '100%',
                      background: '#cdd3cd',
                    }}
                  />
                  <ControlLabel
                    control={
                      <Checkbox
                        value={
                          state.data.campos.microsoftTeams === Toggle.ENABLED
                        }
                        onChange={(value: boolean) => {
                          dataStepSetFilter({
                            microsoftTeams:
                              value === true ? Toggle.ENABLED : Toggle.DISABLED,
                          });
                        }}
                        disabled={completedStep}
                      />
                    }
                    label={dataPrefix.Campos.MicrosoftTeams.Label}
                  />
                </Fragment>
              ) : undefined}
            </Col>
          </Row>
        </Col>
        <Col
          cols={
            ([
              AssemblyModalities.ONLINE,
              AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL,
              AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
              AssemblyModalities.HIBRIDO_SIMULTANEO,
            ].includes(state.data.campos.modality!) &&
              shouldShowQuantityInput &&
              !lessThanCurrentDate) ||
            (shouldShowQuantityInput && lessThanCurrentDate)
              ? [12, 12, 4, 4, 4]
              : !shouldShowQuantityInput &&
                lessThanCurrentDate &&
                ![AssemblyModalities.OFFLINE].includes(
                  state.data.campos.modality!
                )
              ? [12, 12, 6, 6, 6]
              : [12, 12, 12, 12, 12]
          }
        >
          <Input
            data-testid={'input-description'}
            label={dataPrefix.Campos.Descricao.Label}
            placeholder={dataPrefix.Campos.Descricao.Placeholder(props.reu)}
            counter
            maxLength={300}
            value={state.data.campos.description ?? ''}
            onChange={(e) => {
              dataStepSetFilter({
                description: e.target.value,
              });
            }}
            error={sent && !!validator && validator.descriptionIsValid().error}
            helperText={
              sent && !!validator
                ? validator.descriptionIsValid().helperText
                : ''
            }
            disabled={completedStep}
          />
        </Col>
        {[
          AssemblyModalities.ONLINE,
          AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL,
          AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
          AssemblyModalities.HIBRIDO_SIMULTANEO,
        ].includes(state.data.campos.modality!) ? (
          <Fragment>
            {shouldShowQuantityInput ? (
              <Col cols={[12, 12, 4, 4, 4]}>
                <Input
                  data-testid={'input-num-associate'}
                  label={dataPrefix.Campos.QuantidadeAssociados.Label}
                  placeholder={
                    dataPrefix.Campos.QuantidadeAssociados.Placeholder
                  }
                  inputMode={'numeric'}
                  maxLength={5}
                  value={state.data.campos.quantityAssociates ?? ''}
                  onChange={(e) => {
                    dataStepSetFilter({
                      quantityAssociates: onlyNumbers(
                        e.target.value
                      ) as unknown as number,
                    });
                  }}
                  error={
                    sent &&
                    !!validator &&
                    validator.assemblyQuantityAssociateIsValid().error
                  }
                  helperText={
                    sent && !!validator
                      ? validator.assemblyQuantityAssociateIsValid().helperText
                      : ''
                  }
                  disabled={
                    ((state.status === StepStatus.EM_EDICAO ||
                      state.eventStatus === SituationValues.EDITABLE) &&
                      isDateLessThanToday(inputDate)) ||
                    completedStep
                  }
                />
              </Col>
            ) : undefined}

            <Col
              cols={
                shouldShowQuantityInput ? [12, 12, 4, 4, 4] : [12, 12, 6, 6, 6]
              }
            >
              <Input
                data-testid={'input-link'}
                label={dataPrefix.Campos.Link.Label}
                placeholder={dataPrefix.Campos.Link.Label}
                value={state.data.campos.link ?? ''}
                onChange={(e) => {
                  dataStepSetFilter({
                    link: e.target.value,
                  });
                }}
                error={sent && !!validator && validator.linkIsValid().error}
                helperText={
                  sent && !!validator ? validator.linkIsValid().helperText : ''
                }
                disabled={completedStep}
              />
            </Col>
          </Fragment>
        ) : undefined}
        <Col cols={[12, 12, 6, 3, 3]}>
          <Input
            data-testid={'input-date-event'}
            id={'input-date-event'}
            value={state.data.campos.startDate ?? ''}
            onChange={(e) => {
              dataStepSetFilter(
                [
                  AssemblyModalities.HIBRIDO_SIMULTANEO,
                  AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
                ].includes(state.data.campos.modality!) &&
                  !changed.startDateHybrid
                  ? {
                      startDate: formatData(
                        e.target.value.split('-').join('/')
                      ),
                      hybridStartDate: formatData(
                        e.target.value.split('-').join('/')
                      ),
                    }
                  : {
                      startDate: formatData(
                        e.target.value.split('-').join('/')
                      ),
                    }
              );
            }}
            rightObject={
              <DatePicker
                onChange={(date) => {
                  dataStepSetFilter(
                    [
                      AssemblyModalities.HIBRIDO_SIMULTANEO,
                      AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
                    ].includes(state.data.campos.modality!) &&
                      !changed.startDateHybrid
                      ? {
                          startDate: date,
                          hybridStartDate: date,
                        }
                      : {
                          startDate: date,
                        }
                  );
                }}
                queryToAppend={'#input-date-event'}
                initialValue={state.data.campos.startDate ?? ''}
                disabled={completedStep}
                closeOnChange
              />
            }
            label={dataPrefix.Campos.Data.Inicio.Label}
            placeholder={dataPrefix.Campos.Data.Inicio.Placeholder(props.reu)}
            error={sent && !!validator && validator.initialDateIsValid().error}
            helperText={
              sent && !!validator
                ? validator.initialDateIsValid().helperText
                : ''
            }
            disabled={completedStep}
          />
        </Col>
        <Col cols={[12, 12, 6, 3, 3]}>
          <Input
            id={'input-hour-event'}
            data-testid={'input-hour-event'}
            value={state.data.campos.startHour ?? ''}
            onChange={(e) => {
              dataStepSetFilter(
                [
                  AssemblyModalities.HIBRIDO_SIMULTANEO,
                  AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
                ].includes(state.data.campos.modality!) &&
                  !changed.startHourHybrid
                  ? {
                      startHour: formatHora(e.target.value),
                      hybridStartHour: formatHora(e.target.value),
                    }
                  : {
                      startHour: formatHora(e.target.value),
                    }
              );
            }}
            rightObject={
              <IconButton disabled={completedStep} icon={<ClockIcon />} />
            }
            label={dataPrefix.Campos.Data.Inicio.Hora.Label}
            placeholder={dataPrefix.Campos.Data.Inicio.Hora.Placeholder(
              props.reu ? 'reunião' : 'assembleia'
            )}
            error={sent && !!validator && validator.initialHourIsValid().error}
            helperText={
              sent && !!validator
                ? validator.initialHourIsValid().helperText
                : ''
            }
            disabled={completedStep}
          />
        </Col>
        <Col cols={[12, 12, 6, 3, 3]}>
          <Input
            data-testid={'input-end-date-event'}
            id={'input-end-date-event'}
            value={state.data.campos.endDate ?? ''}
            onChange={(e) => {
              dataStepSetFilter(
                [
                  AssemblyModalities.HIBRIDO_SIMULTANEO,
                  AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
                ].includes(state.data.campos.modality!) &&
                  !changed.endDateHybrid
                  ? {
                      endDate: formatData(e.target.value.split('-').join('/')),
                      hybridEndDate: formatData(
                        e.target.value.split('-').join('/')
                      ),
                    }
                  : {
                      endDate: formatData(e.target.value.split('-').join('/')),
                    }
              );
            }}
            rightObject={
              <DatePicker
                disabled={completedStep}
                onChange={(date) => {
                  dataStepSetFilter(
                    [
                      AssemblyModalities.HIBRIDO_SIMULTANEO,
                      AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
                    ].includes(state.data.campos.modality!) &&
                      !changed.endDateHybrid
                      ? {
                          endDate: date,
                          hybridEndDate: date,
                        }
                      : {
                          endDate: date,
                        }
                  );
                }}
                queryToAppend={'#input-end-date-event'}
                initialValue={state.data.campos.endDate ?? ''}
                closeOnChange
              />
            }
            label={dataPrefix.Campos.Data.Fim.Label}
            placeholder={dataPrefix.Campos.Data.Fim.Placeholder(props.reu)}
            error={sent && !!validator && validator.endDateIsValid().error}
            helperText={
              sent && !!validator ? validator.endDateIsValid().helperText : ''
            }
            disabled={completedStep}
          />
        </Col>
        <Col cols={[12, 12, 6, 3, 3]}>
          <Input
            id={'input-end-hour-event'}
            data-testid={'input-end-hour-event'}
            value={state.data.campos.endHour ?? ''}
            onChange={(e) => {
              dataStepSetFilter(
                [
                  AssemblyModalities.HIBRIDO_SIMULTANEO,
                  AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
                ].includes(state.data.campos.modality!) &&
                  !changed.endHourHybrid
                  ? {
                      endHour: formatHora(e.target.value),
                      hybridEndHour: formatHora(e.target.value),
                    }
                  : {
                      endHour: formatHora(e.target.value),
                    }
              );
            }}
            rightObject={
              <IconButton disabled={completedStep} icon={<ClockIcon />} />
            }
            label={dataPrefix.Campos.Data.Fim.Hora.Label}
            placeholder={dataPrefix.Campos.Data.Fim.Hora.Placeholder(props.reu)}
            error={sent && !!validator && validator.endHourIsValid().error}
            helperText={
              sent && !!validator ? validator.endHourIsValid().helperText : ''
            }
            disabled={completedStep}
          />
        </Col>
      </Row>
      <Collapse
        opened={[
          AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL,
          AssemblyModalities.HIBRIDO_SIMULTANEO,
          AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
        ].includes(state.data.campos.modality!)}
        animateOpacity
      >
        <Row>
          <Row className={styles.title_content_hibrid}>
            <Col>
              <Title>
                {[AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL].includes(
                  state.data.campos.modality!
                )
                  ? dataPrefix.TituloHibridoPresencialDigital
                  : [AssemblyModalities.HIBRIDO_SIMULTANEO].includes(
                      state.data.campos.modality!
                    )
                  ? dataPrefix.TituloHibridoSimultaneo
                  : [AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA].includes(
                      state.data.campos.modality!
                    )
                  ? dataPrefix.TituloHibridoDigitalAgencia
                  : ''}
              </Title>
            </Col>
            <Col>
              <p>
                {[AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL].includes(
                  state.data.campos.modality!
                )
                  ? dataPrefix.DescricaoHibridoPresencialDigital
                  : [AssemblyModalities.HIBRIDO_SIMULTANEO].includes(
                      state.data.campos.modality!
                    )
                  ? dataPrefix.DescricaoHibridoSimultaneo
                  : [AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA].includes(
                      state.data.campos.modality!
                    )
                  ? dataPrefix.DescricaoHibridoDigitalAgencia
                  : ''}
              </p>
            </Col>
          </Row>
          <Row>
            <Col cols={[12, 12, 6, 3, 3]}>
              <Input
                data-testid={'input-date-event-hybrid'}
                id={'input-date-event-hybrid'}
                value={
                  ([
                    AssemblyModalities.HIBRIDO_SIMULTANEO,
                    AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
                  ].includes(state.data.campos.modality!) &&
                  !changed.startDateHybrid
                    ? state.data.campos.startDate
                    : state.data.campos.hybridStartDate) ?? ''
                }
                onChange={(e) => {
                  setChanged({ ...changed, startDateHybrid: true });
                  dataStepSetFilter({
                    hybridStartDate: formatData(
                      e.target.value.split('-').join('/')
                    ),
                  });
                }}
                rightObject={
                  <DatePicker
                    onChange={(date) => {
                      setChanged({ ...changed, startDateHybrid: true });
                      dataStepSetFilter({
                        hybridStartDate: date,
                      });
                    }}
                    queryToAppend={'#input-date-event-hybrid'}
                    initialValue={
                      ([
                        AssemblyModalities.HIBRIDO_SIMULTANEO,
                        AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
                      ].includes(state.data.campos.modality!) &&
                      !changed.startDateHybrid
                        ? state.data.campos.startDate || ''
                        : state.data.campos.hybridStartDate || '') ?? ''
                    }
                    closeOnChange
                    disabled={completedStep}
                  />
                }
                label={dataPrefix.Campos.DataHibrido.Inicio.Label}
                placeholder={dataPrefix.Campos.DataHibrido.Inicio.Placeholder}
                error={
                  sent &&
                  !!validator &&
                  validator.initialDateHibridIsValid().error
                }
                helperText={
                  sent && !!validator
                    ? validator.initialDateHibridIsValid().helperText
                    : ''
                }
                disabled={completedStep}
              />
            </Col>
            <Col cols={[12, 12, 6, 3, 3]}>
              <Input
                data-testid={'input-hour-event-hybrid'}
                id={'input-hour-event-hybrid'}
                value={
                  ([
                    AssemblyModalities.HIBRIDO_SIMULTANEO,
                    AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
                  ].includes(state.data.campos.modality!) &&
                  !changed.startHourHybrid
                    ? state.data.campos.startHour || ''
                    : state.data.campos.hybridStartHour || '') ?? ''
                }
                onChange={(e) => {
                  setChanged({ ...changed, startHourHybrid: true });
                  dataStepSetFilter({
                    hybridStartHour: formatHora(e.target.value),
                  });
                }}
                rightObject={
                  <IconButton disabled={completedStep} icon={<ClockIcon />} />
                }
                label={dataPrefix.Campos.DataHibrido.Inicio.Hora.Label}
                placeholder={dataPrefix.Campos.DataHibrido.Inicio.Hora.Label}
                error={
                  sent &&
                  !!validator &&
                  validator.initialHourHibridIsValid().error
                }
                helperText={
                  sent && !!validator
                    ? validator.initialHourHibridIsValid().helperText
                    : ''
                }
                disabled={completedStep}
              />
            </Col>
            <Col cols={[12, 12, 6, 3, 3]}>
              <Input
                data-testid={'input-end-date-event-hybrid'}
                id={'input-end-date-event-hybrid'}
                value={
                  ([
                    AssemblyModalities.HIBRIDO_SIMULTANEO,
                    AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
                  ].includes(state.data.campos.modality!) &&
                  !changed.endDateHybrid
                    ? state.data.campos.endDate
                    : state.data.campos.hybridEndDate) ?? ''
                }
                onChange={(e) => {
                  setChanged({ ...changed, endDateHybrid: true });
                  dataStepSetFilter({
                    hybridEndDate: formatData(
                      e.target.value.split('-').join('/')
                    ),
                  });
                }}
                rightObject={
                  <DatePicker
                    onChange={(date) => {
                      setChanged({ ...changed, endDateHybrid: true });
                      dataStepSetFilter({
                        hybridEndDate: date,
                      });
                    }}
                    queryToAppend={'#input-end-date-event-hybrid'}
                    initialValue={
                      ([
                        AssemblyModalities.HIBRIDO_SIMULTANEO,
                        AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
                      ].includes(state.data.campos.modality!) &&
                      !changed.endDateHybrid
                        ? state.data.campos.endDate || ''
                        : state.data.campos.hybridEndDate || '') ?? ''
                    }
                    closeOnChange
                    disabled={completedStep}
                  />
                }
                label={dataPrefix.Campos.DataHibrido.Fim.Label}
                placeholder={dataPrefix.Campos.DataHibrido.Fim.Placeholder}
                error={
                  sent && !!validator && validator.endDateHibridIsValid().error
                }
                helperText={
                  sent && !!validator
                    ? validator.endDateHibridIsValid().helperText
                    : ''
                }
                disabled={completedStep}
              />
            </Col>
            <Col cols={[12, 12, 6, 3, 3]}>
              <Input
                data-testid={'input-end-hour-event-hybrid'}
                id={'input-end-hour-event-hybrid'}
                value={
                  ([
                    AssemblyModalities.HIBRIDO_SIMULTANEO,
                    AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
                  ].includes(state.data.campos.modality!) &&
                  !changed.endHourHybrid
                    ? state.data.campos.endHour || ''
                    : state.data.campos.hybridEndHour || '') ?? ''
                }
                onChange={(e) => {
                  setChanged({ ...changed, endHourHybrid: true });
                  dataStepSetFilter({
                    hybridEndHour: formatHora(e.target.value),
                  });
                }}
                rightObject={
                  <IconButton disabled={completedStep} icon={<ClockIcon />} />
                }
                label={dataPrefix.Campos.DataHibrido.Fim.Hora.Label}
                placeholder={dataPrefix.Campos.DataHibrido.Fim.Hora.Placeholder}
                error={
                  sent && !!validator && validator.endHourHibridIsValid().error
                }
                helperText={
                  sent && !!validator
                    ? validator.endHourHibridIsValid().helperText
                    : ''
                }
                disabled={completedStep}
              />
            </Col>
          </Row>
        </Row>
      </Collapse>

      <Row>
        <Col buttonActionsCol end>
          <Button
            data-testid={'button-save'}
            theme={ButtonThemes.Primary}
            loading={loading}
            onClick={
              [StepStatus.EM_EDICAO, StepStatus.CRIACAO].includes(
                state.status
              ) && state.data.campos.flgEventTest
                ? eventTestModal
                : handleSubmit
            }
            disabled={
              loadingData ||
              loading ||
              (sent && !!validator && !validator.allIsValid())
            }
            leftIcon={<CheckCircleIcon />}
          >
            {completedStep
              ? dataPrefix.Buttons.Prosseguir
              : dataPrefix.Buttons.Salvar}
          </Button>
        </Col>
      </Row>
    </Row>
  );
}
